import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faMinus,
  faPlus,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useState } from "react";
import { Layer, Source, SourceProps } from "react-map-gl/maplibre";
import { ColorBox } from "../../components/ColorBox";
import { SourceLayer, useCaMap, useCode, useLayers } from "../../state";
import { GroupedCodesDTO } from "../../state/types";
import { Layers } from "../Layers";
import { CustomControl } from "./CustomControl";

const IDENTBG = "LayersControlBG";
const IDENT = "LayersControl";

function LayerSubItem({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-50">
      <button
        className="w-full flex flex-row justify-between items-center px-3 py-2 text-sm font-medium text-dark-blue hover:bg-gray-50"
        onClick={() => setOpen(!open)}
      >
        <span className="mr-2">{title}</span>
        <FontAwesomeIcon
          icon={open ? faMinus : faPlus}
          className="text-xs text-gray-400 flex-shrink-0"
        />
      </button>
      {open && <div className="px-4 py-2 text-xs text-gray-600 bg-gray-50">{children}</div>}
    </div>
  );
}

const Sub = ({ children }) => (
  <div className="border-b border-gray-50">
    <span className="w-full flex flex-row justify-between items-center px-3 py-2 text-sm text-dark-blue hover:bg-gray-50">
      {children}
    </span>
  </div>
);

const Legend = ({ codes }) =>
  (codes?.length && (
    <LayerSubItem title="Legenda">
      <ul className="list-none p-0 m-0 space-y-1">
        {codes.map((c, i) => (
          <li key={i} className="flex items-center text-xs text-gray-700">
            <ColorBox color={c.color || "#234567"} />
            <span className="ml-2">{c.name}</span>
          </li>
        ))}
      </ul>
    </LayerSubItem>
  )) ||
  null;

function LayerItem({
  layer,
  codes,
  opacity,
  setOpacity,
  initialOpen = false,
}: {
  layer: SourceLayer;
  codes: GroupedCodesDTO[];
  opacity: number;
  setOpacity: (o: number) => void;
  initialOpen: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  const toggle = useLayers((s) => s.toggle);
  const move = useLayers((s) => s.move);
  const selected = useLayers((s) => s.selected);
  const index = selected.indexOf(layer);
  return (
    <div className="bg-white rounded-lg shadow-md mb-1 overflow-hidden border border-gray-100">
      <div
        className="cursor-pointer w-full flex justify-between items-center p-2 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-dark-blue mr-2">{layer.title}</span>
        <div className="flex items-center gap-2">
          {/* omhoog in de lijst = hoger op de kaart = verderop in `selected` */}
          <button
            title="Omhoog"
            disabled={index >= selected.length - 1}
            className="disabled:opacity-25"
            onClick={(e) => {
              e.stopPropagation();
              move(layer, 1);
            }}
          >
            <FontAwesomeIcon icon={faArrowUp} className="text-gray-400 flex-shrink-0" />
          </button>
          <button
            title="Omlaag"
            disabled={index <= 0}
            className="disabled:opacity-25"
            onClick={(e) => {
              e.stopPropagation();
              move(layer, -1);
            }}
          >
            <FontAwesomeIcon icon={faArrowDown} className="text-gray-400 flex-shrink-0" />
          </button>
          <button
            title="Verwijderen"
            onClick={(e) => {
              e.stopPropagation();
              toggle(layer);
            }}
          >
            <FontAwesomeIcon icon={faTrashCan} className="text-gray-400 flex-shrink-0" />
          </button>
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className="text-gray-400 flex-shrink-0"
          />
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100">
          <Sub>
            Transparantie
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="ml-2 w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-dark-blue"
            />
          </Sub>
          <Legend codes={codes} />
        </div>
      )}
    </div>
  );
}

// Vastgepind onderpaneel met de actieve lagen: legenda, transparantie en
// volgorde. Bovenste in de lijst = bovenste laag op de kaart.
function ActiveLayers({
  layers,
  codesPerLayer,
}: {
  layers: SourceLayer[];
  codesPerLayer: { [k: string]: GroupedCodesDTO[] };
}) {
  const [open, setOpen] = useState(true);
  const opacities = useLayers((s) => s.opacities);
  const setOpacity = useLayers((s) => s.setOpacity);
  if (!layers.length) return null;
  return (
    <div className="border-t border-sidebar-menu-border bg-sidebar-menu shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
      <button
        className="w-full flex justify-between items-center px-4 py-2 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-dark-blue">Actieve lagen ({layers.length})</span>
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronUp} className="text-gray-400" />
      </button>
      {open && (
        <div className="px-3 max-h-[40vh] overflow-y-auto">
          {[...layers].reverse().map((layer) => (
            <LayerItem
              key={layer.name}
              layer={layer}
              codes={codesPerLayer[layer.name]}
              opacity={opacities[layer.name] ?? 0.8}
              setOpacity={(o) => setOpacity(layer.name, o)}
              initialOpen={layers.length === 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PopupControl({
  layers,
  codesPerLayer,
}: {
  layers: SourceLayer[];
  codesPerLayer: { [k: string]: GroupedCodesDTO[] };
}) {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <>
      <CustomControl position="top-right" className="maplibregl-ctrl maplibregl-ctrl-group">
        <button
          className="maplibregl-ctrl-button maplibregl-ctrl-layers relative"
          type="button"
          title="Kaartlagen"
          aria-label="Kaartlagen"
          onClick={toggleOpen}
        >
          <span className="maplibregl-ctrl-icon" aria-hidden="true" />
          {layers.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {layers.length}
            </span>
          )}
        </button>
      </CustomControl>
      {open && (
        <div className="fixed right-0 top-14 bottom-0 z-10 w-[calc(100vw-2.5rem)] sm:w-[28rem] flex flex-col bg-sidebar-menu border-l border-sidebar-menu-border">
          <div className="flex-1 overflow-y-auto">
            <Layers onClose={toggleOpen} />
          </div>
          <ActiveLayers layers={layers} codesPerLayer={codesPerLayer} />
        </div>
      )}
    </>
  );
}

interface LayerProps {
  source: string;
  opacity: number;
}
const BlackLayer = ({ source, opacity }: LayerProps) => (
  <Layer
    id={source + "_black"}
    source={source}
    source-layer="default"
    beforeId="parcel"
    type="line"
    paint={{
      "line-color": "black",
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 2, 18, 4],
      "line-opacity": opacity,
    }}
  />
);

const ColoredLayer = ({
  source,
  opacity,
  codes,
}: LayerProps & {
  codes: GroupedCodesDTO[];
}) => {
  if (!codes.length) return null;
  const matchListColor = [
    ...Object.values(codes).reduce(
      (acc, e) => [...acc, e.codes, e.color || "#234567"],
      ["match", ["get", "code_id"]]
    ),
    "rgba(0,0,0, 0.3)",
  ];

  return (
    <Layer
      id={source + "_colored"}
      source={source}
      source-layer="default"
      beforeId="parcel"
      type="fill"
      paint={{
        "fill-outline-color": "rgb(7, 119, 53)",
        // @ts-ignore
        "fill-color": matchListColor,
        "fill-opacity": opacity,
      }}
    />
  );
};

const LabelLayer = ({
  source,
  opacity,
  type,
}: LayerProps & {
  type: string;
}) => (
  <Layer
    id={source + "_label"}
    source={source}
    source-layer="default"
    beforeId="parcel"
    type="symbol"
    layout={{
      "text-rotation-alignment": "map",
      "text-anchor": "top",
      "text-field": "{name}",
      "text-font": ["Noto Sans Regular"],
      "text-size": 14,
      "symbol-placement": type === "Road" ? "line" : "point",
    }}
    minzoom={7}
    maxzoom={type === "Road" ? 24 : 15}
    paint={{
      "text-color": "#000",
      "text-halo-blur": 1,
      "text-halo-color": "rgba(255,255,255,0.75)",
      "text-halo-width": 1,
      "text-opacity": opacity,
    }}
  />
);

const OverlayLayer = ({
  layer,
  codesPerLayer,
}: {
  layer: SourceLayer;
  codesPerLayer: { [k: string]: GroupedCodesDTO[] };
}) => {
  const id = layer.name;
  const codes = codesPerLayer[id];
  const isBlack = layer.type === "vector" && !codes?.length;
  const opacities = useLayers((s) => s.opacities);
  const opacity = opacities[id] ?? (isBlack ? 1 : 0.5);
  const props: SourceProps = {
    id: IDENT + id,
    type: layer.type,
    url: layer.url,
    promoteId: "id",
  };
  const lProps = { source: props.id, opacity: opacity };
  return (
    <Source {...props}>
      {layer.labels && <LabelLayer type={layer.type} {...lProps} />}
      {layer.type === "raster" ? (
        <Layer
          id={props.id}
          type="raster"
          beforeId="parcel"
          {...lProps}
          paint={{ "raster-opacity": opacity }}
        />
      ) : !isBlack ? (
        <ColoredLayer codes={codesPerLayer[id]} {...lProps} />
      ) : (
        <BlackLayer {...lProps} />
      )}
    </Source>
  );
};

export const LayersControl = () => {
  const map = useCaMap();
  const { backgroundId, layers, selected } = useLayers();
  const { codesPerLayer, fetchMissingCodes } = useCode();
  if (!map) return null;

  let background;
  if (backgroundId !== "default") {
    const bg = layers.find((l) => l.name === backgroundId);
    if (bg) {
      background = {
        type: "raster",
        tiles: [bg.url],
        tileSize: 256,
        ...bg.attributes,
      };
    }
  }

  if (selected?.length) fetchMissingCodes(selected);

  return (
    <>
      {background && (
        <Source id={IDENTBG} {...background}>
          <Layer id={IDENTBG} type="raster" source={IDENTBG} beforeId="parcel" />
        </Source>
      )}
      {selected.map((layer, i) => (
        // index in de key: bij herordenen remounten zodat de kaart-stapel
        // de array-volgorde volgt (react-map-gl verplaatst lagen niet zelf)
        <OverlayLayer key={`${i}-${layer.name}`} layer={layer} codesPerLayer={codesPerLayer} />
      ))}
      <PopupControl layers={selected} codesPerLayer={codesPerLayer} />
    </>
  );
};
